import { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Site, SiteTicket, SiteSnapshot, FileInfo, FileContent,
  getSite, listFiles, getFile, putFile, deleteFile,
  listTickets, createTicket, listSnapshots, createSnapshot, restoreSnapshot,
  invalidateCache, verifyDNS, 
} from '../api/sites';

// Tab type
type Tab = 'files' | 'tickets' | 'snapshots' | 'settings';

// Site detail page with file browser [FR-SITE-001, FR-SITE-004]
export function SiteDetailPage() {
  const { domain } = useParams<{ domain: string }>();
  const [site, setSite] = useState<Site | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<Tab>('files');

  useEffect(() => {
    if (domain) {
      loadSite();
    }
  }, [domain]);

  async function loadSite() {
    try {
      setLoading(true);
      const data = await getSite(domain!);
      setSite(data);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load site');
    } finally {
      setLoading(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-ds-primary"></div>
      </div>
    );
  }

  if (error || !site) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-red-700">{error || 'Site not found'}</p>
          <Link to="/sites" className="mt-2 text-red-600 hover:text-red-800 text-sm">
            ← Back to sites
          </Link>
        </div>
      </div>
    );
  }

  const tabs: { id: Tab; label: string }[] = [
    { id: 'files', label: 'Files' },
    { id: 'tickets', label: 'Virtual Webmaster' },
    { id: 'snapshots', label: 'Snapshots' },
    { id: 'settings', label: 'Settings' },
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center space-x-2 text-sm text-gray-500 mb-2">
          <Link to="/sites" className="hover:text-ds-primary">Sites</Link>
          <span>/</span>
          <span>{site.domain}</span>
        </div>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{site.domain}</h1>
            <p className="mt-1 text-sm text-gray-500">
              Generation {site.generation} • {site.status.replace('_', ' ')}
            </p>
          </div>
          <div className="flex space-x-3">
            {site.cf_distribution_id && (
              <InvalidateCacheButton domain={domain!} />
            )}
            {!site.dns_configured && (
              <VerifyDNSButton domain={domain!} onVerified={loadSite} />
            )}
          </div>
        </div>
      </div>

      {/* DNS Setup Banner */}
      {site.status === 'pending_dns' && site.nameservers && (
        <div className="mb-6 bg-yellow-50 border border-yellow-200 rounded-md p-4">
          <h3 className="text-sm font-medium text-yellow-800">DNS Configuration Required</h3>
          <p className="mt-1 text-sm text-yellow-700">
            Configure these nameservers at your domain registrar:
          </p>
          <ul className="mt-2 text-sm font-mono text-yellow-800 space-y-1">
            {site.nameservers.map((ns) => (
              <li key={ns}>{ns}</li>
            ))}
          </ul>
          <p className="mt-3 text-sm text-yellow-700">
            After updating, click "Verify DNS" to complete setup.
          </p>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.id
                  ? 'border-ds-primary text-ds-primary'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'files' && <FileBrowser domain={domain!} />}
      {activeTab === 'tickets' && <TicketsPanel domain={domain!} />}
      {activeTab === 'snapshots' && <SnapshotsPanel domain={domain!} />}
      {activeTab === 'settings' && <SettingsPanel site={site} />}
    </div>
  );
}

// File Browser Component [FR-SITE-004]
function FileBrowser({ domain }: { domain: string }) {
  const [currentPath, setCurrentPath] = useState('');
  const [files, setFiles] = useState<FileInfo[]>([]);
  const [selectedFile, setSelectedFile] = useState<FileContent | null>(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editContent, setEditContent] = useState('');
  const [saving, setSaving] = useState(false);

  const loadFiles = useCallback(async () => {
    try {
      setLoading(true);
      const data = await listFiles(domain, currentPath);
      setFiles(data || []);
    } catch (err) {
      console.error('Failed to load files:', err);
    } finally {
      setLoading(false);
    }
  }, [domain, currentPath]);

  useEffect(() => {
    loadFiles();
  }, [loadFiles]);

  async function handleFileClick(file: FileInfo) {
    if (file.is_dir) {
      setCurrentPath(currentPath ? `${currentPath}/${file.name}` : file.name);
      setSelectedFile(null);
    } else {
      try {
        const path = currentPath ? `${currentPath}/${file.name}` : file.name;
        const content = await getFile(domain, path);
        setSelectedFile(content);
        setEditContent(content.content);
        setEditing(false);
      } catch (err) {
        console.error('Failed to load file:', err);
      }
    }
  }

  function navigateUp() {
    const parts = currentPath.split('/');
    parts.pop();
    setCurrentPath(parts.join('/'));
    setSelectedFile(null);
  }

  async function handleSave() {
    if (!selectedFile) return;
    try {
      setSaving(true);
      await putFile(domain, selectedFile.path, editContent);
      setSelectedFile({ ...selectedFile, content: editContent });
      setEditing(false);
    } catch (err) {
      console.error('Failed to save file:', err);
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(file: FileInfo) {
    if (!confirm(`Delete ${file.name}?`)) return;
    try {
      const path = currentPath ? `${currentPath}/${file.name}` : file.name;
      await deleteFile(domain, path);
      await loadFiles();
      if (selectedFile?.path === path) {
        setSelectedFile(null);
      }
    } catch (err) {
      console.error('Failed to delete file:', err);
    }
  }

  const breadcrumbs = currentPath ? currentPath.split('/') : [];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* File List */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        {/* Breadcrumb */}
        <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
          <div className="flex items-center text-sm">
            <button
              onClick={() => { setCurrentPath(''); setSelectedFile(null); }}
              className="text-ds-primary hover:text-ds-primary-dark"
            >
              root
            </button>
            {breadcrumbs.map((part, idx) => (
              <span key={idx} className="flex items-center">
                <span className="mx-2 text-gray-400">/</span>
                <button
                  onClick={() => {
                    setCurrentPath(breadcrumbs.slice(0, idx + 1).join('/'));
                    setSelectedFile(null);
                  }}
                  className="text-ds-primary hover:text-ds-primary-dark"
                >
                  {part}
                </button>
              </span>
            ))}
          </div>
        </div>

        {/* File List */}
        <div className="divide-y divide-gray-200 max-h-[500px] overflow-y-auto">
          {currentPath && (
            <button
              onClick={navigateUp}
              className="w-full px-4 py-3 flex items-center hover:bg-gray-50 text-left"
            >
              <span className="text-gray-400 mr-3">📁</span>
              <span className="text-gray-600">..</span>
            </button>
          )}
          {loading ? (
            <div className="px-4 py-8 text-center text-gray-500">Loading...</div>
          ) : files.length === 0 ? (
            <div className="px-4 py-8 text-center text-gray-500">No files</div>
          ) : (
            files.map((file) => (
              <div
                key={file.name}
                className="px-4 py-3 flex items-center justify-between hover:bg-gray-50 group"
              >
                <button
                  onClick={() => handleFileClick(file)}
                  className="flex items-center flex-1 text-left"
                >
                  <span className="mr-3">{file.is_dir ? '📁' : '📄'}</span>
                  <span className="text-gray-900">{file.name}</span>
                  {!file.is_dir && file.size !== undefined && (
                    <span className="ml-2 text-xs text-gray-400">
                      {formatFileSize(file.size)}
                    </span>
                  )}
                </button>
                {!file.is_dir && (
                  <button
                    onClick={() => handleDelete(file)}
                    className="text-red-500 opacity-0 group-hover:opacity-100 hover:text-red-700"
                  >
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                )}
              </div>
            ))
          )}
        </div>
      </div>

      {/* File Editor */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        {selectedFile ? (
          <>
            <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
              <div>
                <span className="font-medium text-gray-900">{selectedFile.path}</span>
                <span className="ml-2 text-xs text-gray-500">{selectedFile.content_type}</span>
              </div>
              <div className="flex space-x-2">
                {editing ? (
                  <>
                    <button
                      onClick={() => { setEditing(false); setEditContent(selectedFile.content); }}
                      className="px-3 py-1 text-sm text-gray-600 hover:text-gray-800"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSave}
                      disabled={saving}
                      className="px-3 py-1 text-sm bg-ds-primary text-white rounded hover:bg-ds-primary-dark disabled:opacity-50"
                    >
                      {saving ? 'Saving...' : 'Save'}
                    </button>
                  </>
                ) : (
                  <button
                    onClick={() => setEditing(true)}
                    className="px-3 py-1 text-sm bg-ds-primary text-white rounded hover:bg-ds-primary-dark"
                  >
                    Edit
                  </button>
                )}
              </div>
            </div>
            <div className="p-4">
              {editing ? (
                <textarea
                  value={editContent}
                  onChange={(e) => setEditContent(e.target.value)}
                  className="w-full h-[400px] font-mono text-sm border border-gray-300 rounded p-2 focus:outline-none focus:ring-2 focus:ring-ds-primary"
                />
              ) : (
                <pre className="text-sm font-mono overflow-auto max-h-[400px] whitespace-pre-wrap">
                  {selectedFile.content}
                </pre>
              )}
            </div>
          </>
        ) : (
          <div className="flex items-center justify-center h-[400px] text-gray-500">
            Select a file to view
          </div>
        )}
      </div>
    </div>
  );
}

// Tickets Panel [FR-SITE-002]
function TicketsPanel({ domain }: { domain: string }) {
  const [tickets, setTickets] = useState<SiteTicket[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    loadTickets();
  }, [domain]);

  async function loadTickets() {
    try {
      setLoading(true);
      const data = await listTickets(domain);
      setTickets(data || []);
    } catch (err) {
      console.error('Failed to load tickets:', err);
    } finally {
      setLoading(false);
    }
  }

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    try {
      setCreating(true);
      await createTicket(domain, { title, description });
      setTitle('');
      setDescription('');
      setShowCreate(false);
      await loadTickets();
    } catch (err) {
      console.error('Failed to create ticket:', err);
    } finally {
      setCreating(false);
    }
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-lg font-medium text-gray-900">Virtual Webmaster</h2>
          <p className="text-sm text-gray-500">Request changes to your site</p>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="px-4 py-2 text-sm bg-ds-primary text-white rounded-md hover:bg-ds-primary-dark"
        >
          New Request
        </button>
      </div>

      {showCreate && (
        <div className="mb-6 bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-medium mb-4">New Change Request</h3>
          <form onSubmit={handleCreate}>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">What do you need?</label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="e.g., Update business hours on homepage"
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-ds-primary focus:ring-ds-primary"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Details</label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Provide any specific details, text, or instructions..."
                  rows={4}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-ds-primary focus:ring-ds-primary"
                />
              </div>
            </div>
            <div className="mt-6 flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => setShowCreate(false)}
                className="px-4 py-2 text-sm text-gray-700 hover:text-gray-900"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={creating || !title.trim()}
                className="px-4 py-2 text-sm bg-ds-primary text-white rounded-md hover:bg-ds-primary-dark disabled:opacity-50"
              >
                {creating ? 'Submitting...' : 'Submit Request'}
              </button>
            </div>
          </form>
        </div>
      )}

      {loading ? (
        <div className="text-center py-8 text-gray-500">Loading...</div>
      ) : tickets.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
          <p className="text-gray-500">No change requests yet</p>
        </div>
      ) : (
        <div className="space-y-4">
          {tickets.map((ticket) => (
            <div key={ticket.dskanban_issue_id} className="bg-white rounded-lg border border-gray-200 p-4">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-medium text-gray-900">{ticket.title}</h3>
                  <p className="text-sm text-gray-500 mt-1">
                    Created {new Date(ticket.created_at).toLocaleDateString()}
                  </p>
                </div>
                <span className={`px-2 py-1 text-xs rounded-full ${
                  ticket.status === 'done' ? 'bg-green-100 text-green-800' :
                  ticket.status === 'in_progress' ? 'bg-blue-100 text-blue-800' :
                  'bg-gray-100 text-gray-800'
                }`}>
                  {ticket.status}
                </span>
              </div>
              {ticket.snapshot_s3_key && (
                <p className="mt-2 text-xs text-gray-400">
                  Snapshot: {ticket.snapshot_s3_key.split('/').pop()}
                </p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Snapshots Panel [FR-SITE-003]
function SnapshotsPanel({ domain }: { domain: string }) {
  const [snapshots, setSnapshots] = useState<SiteSnapshot[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [restoring, setRestoring] = useState<string | null>(null);

  useEffect(() => {
    loadSnapshots();
  }, [domain]);

  async function loadSnapshots() {
    try {
      setLoading(true);
      const data = await listSnapshots(domain);
      setSnapshots(data || []);
    } catch (err) {
      console.error('Failed to load snapshots:', err);
    } finally {
      setLoading(false);
    }
  }

  async function handleCreateSnapshot() {
    try {
      setCreating(true);
      await createSnapshot(domain);
      await loadSnapshots();
    } catch (err) {
      console.error('Failed to create snapshot:', err);
    } finally {
      setCreating(false);
    }
  }

  async function handleRestore(snapshot: SiteSnapshot) {
    if (!confirm(`Restore site to ${new Date(snapshot.created_at).toLocaleString()}? A backup will be created first.`)) {
      return;
    }

    try {
      setRestoring(snapshot.s3_key);
      await restoreSnapshot(domain, snapshot.s3_key);
      alert('Snapshot restored successfully! A backup was created before restore.');
      await loadSnapshots();
    } catch (err) {
      alert('Failed to restore snapshot: ' + (err instanceof Error ? err.message : 'Unknown error'));
    } finally {
      setRestoring(null);
    }
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-lg font-medium text-gray-900">Snapshots</h2>
          <p className="text-sm text-gray-500">Point-in-time backups (30-day retention)</p>
        </div>
        <button
          onClick={handleCreateSnapshot}
          disabled={creating}
          className="px-4 py-2 text-sm bg-ds-primary text-white rounded-md hover:bg-ds-primary-dark disabled:opacity-50"
        >
          {creating ? 'Creating...' : 'Create Snapshot'}
        </button>
      </div>

      {loading ? (
        <div className="text-center py-8 text-gray-500">Loading...</div>
      ) : snapshots.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
          <p className="text-gray-500">No snapshots yet</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 divide-y divide-gray-200">
          {snapshots.map((snapshot) => (
            <div key={snapshot.s3_key} className="p-4 flex items-center justify-between">
              <div>
                <p className="font-medium text-gray-900">
                  {new Date(snapshot.created_at).toLocaleString()}
                </p>
                <p className="text-sm text-gray-500">
                  {formatFileSize(snapshot.size_bytes)} • {snapshot.trigger}
                  {snapshot.ticket_id && ` • Ticket #${snapshot.ticket_id}`}
                </p>
              </div>
              <div className="flex space-x-3">
                <button
                  onClick={() => handleRestore(snapshot)}
                  disabled={restoring === snapshot.s3_key}
                  className="text-sm text-ds-primary hover:text-ds-primary-dark disabled:opacity-50"
                >
                  {restoring === snapshot.s3_key ? 'Restoring...' : 'Restore'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Settings Panel
function SettingsPanel({ site }: { site: Site }) {
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <h2 className="text-lg font-medium text-gray-900 mb-6">Site Settings</h2>
      
      <dl className="space-y-4">
        <div className="grid grid-cols-3 gap-4">
          <dt className="text-sm font-medium text-gray-500">Domain</dt>
          <dd className="text-sm text-gray-900 col-span-2">{site.domain}</dd>
        </div>
        <div className="grid grid-cols-3 gap-4">
          <dt className="text-sm font-medium text-gray-500">S3 Bucket</dt>
          <dd className="text-sm text-gray-900 col-span-2 font-mono">{site.s3_bucket}</dd>
        </div>
        <div className="grid grid-cols-3 gap-4">
          <dt className="text-sm font-medium text-gray-500">S3 Prefix</dt>
          <dd className="text-sm text-gray-900 col-span-2 font-mono">{site.s3_prefix}</dd>
        </div>
        {site.cf_distribution_id && (
          <div className="grid grid-cols-3 gap-4">
            <dt className="text-sm font-medium text-gray-500">CloudFront</dt>
            <dd className="text-sm text-gray-900 col-span-2 font-mono">{site.cf_distribution_id}</dd>
          </div>
        )}
        {site.route53_zone_id && (
          <div className="grid grid-cols-3 gap-4">
            <dt className="text-sm font-medium text-gray-500">Route53 Zone</dt>
            <dd className="text-sm text-gray-900 col-span-2 font-mono">{site.route53_zone_id}</dd>
          </div>
        )}
        <div className="grid grid-cols-3 gap-4">
          <dt className="text-sm font-medium text-gray-500">Generation</dt>
          <dd className="text-sm text-gray-900 col-span-2">{site.generation}</dd>
        </div>
        <div className="grid grid-cols-3 gap-4">
          <dt className="text-sm font-medium text-gray-500">Created</dt>
          <dd className="text-sm text-gray-900 col-span-2">
            {new Date(site.created_at).toLocaleString()}
          </dd>
        </div>
      </dl>
    </div>
  );
}

// Helper Components
function InvalidateCacheButton({ domain }: { domain: string }) {
  const [loading, setLoading] = useState(false);

  async function handleClick() {
    try {
      setLoading(true);
      const result = await invalidateCache(domain);
      alert(`Cache invalidation started: ${result.invalidation_id}`);
    } catch (err) {
      alert('Failed to invalidate cache');
    } finally {
      setLoading(false);
    }
  }

  return (
    <button
      onClick={handleClick}
      disabled={loading}
      className="px-4 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
    >
      {loading ? 'Invalidating...' : 'Invalidate Cache'}
    </button>
  );
}

function VerifyDNSButton({ domain, onVerified }: { domain: string; onVerified: () => void }) {
  const [loading, setLoading] = useState(false);

  async function handleClick() {
    try {
      setLoading(true);
      const result = await verifyDNS(domain);
      if (result.verified) {
        alert('DNS verified! CloudFront distribution being created.');
        onVerified();
      } else {
        alert('DNS not yet propagated. Please wait and try again.');
      }
    } catch (err) {
      alert('Failed to verify DNS');
    } finally {
      setLoading(false);
    }
  }

  return (
    <button
      onClick={handleClick}
      disabled={loading}
      className="px-4 py-2 text-sm bg-yellow-500 text-white rounded-md hover:bg-yellow-600 disabled:opacity-50"
    >
      {loading ? 'Verifying...' : 'Verify DNS'}
    </button>
  );
}

// Utils
function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
